import type {CSSProperties} from 'react';
import type {Editor} from '@tiptap/react';
import {SLIDE_MONOSPACE_FONT_FAMILY} from '../shared/slideTheme';

/** A selectable font for slide text. */
export interface SlideFontOption {
  /** Human-readable name shown in the dropdown. */
  label: string;
  /**
   * CSS `font-family` value applied via the TipTap `FontFamily` mark (see
   * `shared/textExtensions.ts`). May be a fallback stack so the text still
   * resolves to a reasonable face on laptops lacking the preferred family. The
   * empty string is the "Default" sentinel: picking it clears the mark so the
   * text falls back to the element-level `SLIDE_FONT_FAMILY` (see
   * `shared/slideTheme.ts`).
   */
  family: string;
}

/**
 * Fonts offered for slide text. "Default" clears the per-run font so the text
 * inherits the deck's default (`SLIDE_FONT_FAMILY`, a `KaTeX_Main` TeX face).
 * The rest:
 *  - `KaTeX` — the same classic TeX/LaTeX book face as the default, offered
 *    explicitly so a run can opt back into it after another font was applied.
 *  - `Code` — the monospace stack Monaco (the code editor) uses by default,
 *    extended with Windows/Linux equivalents so it resolves on any laptop.
 *  - `Futura` — the geometric sans display face. Futura is a system font only
 *    on macOS, so this falls back to the self-hosted `Jost` (a Futura revival)
 *    on non-Apple laptops (registered in `shared/slideTheme.ts`).
 *  - `Chalkboard` — macOS's `Chalkboard SE`, falling back to the self-hosted
 *    `Comic Neue` on non-Apple laptops (registered in `shared/slideTheme.ts`).
 *
 * Exported for reuse anywhere the same font choices should be offered (e.g.
 * PAI's splash markup overlay), so the option list stays in one place.
 */
export const SLIDE_FONTS: SlideFontOption[] = [
  {label: 'Default', family: ''},
  {label: 'KaTeX', family: "'KaTeX_Main', Georgia, 'Times New Roman', serif"},
  {
    label: 'Code',
    family: SLIDE_MONOSPACE_FONT_FAMILY,
  },
  {
    label: 'Futura',
    family: "Futura, Jost, 'Century Gothic', sans-serif",
  },
  {
    label: 'Chalkboard',
    family: "'Chalkboard SE', Chalkboard, 'Comic Neue', 'Comic Sans MS', cursive",
  },
];

const SELECT_STYLE: CSSProperties = {
  border: '1px solid #ccc',
  borderRadius: 4,
  padding: '4px 6px',
  fontSize: 12,
};

/** Props for {@link FontFamilyControl}. */
interface FontFamilyControlProps {
  /**
   * The live TipTap editor for whatever's currently being edited (a text
   * element, table cell, or shape text — see `Toolbar.tsx`'s wiring — or a
   * lesson prompt field, see `lessonEditor/editors/RichTextFieldEditor.tsx`).
   * The font is applied to the editor's current selection (a `fontFamily`
   * mark), with no element-level fallback the way font size/color have one,
   * so the control is disabled while this is undefined (selected but not
   * actively being edited).
   */
  editor: Editor | undefined;
}

/**
 * Ribbon dropdown for the font of the current text selection. Sets (or, for
 * "Default", clears) the TipTap `fontFamily` mark on the selection — see
 * `shared/textExtensions.ts`. A run with no mark inherits the deck default
 * `SLIDE_FONT_FAMILY`, so an unfamiliar/absent stored value renders as
 * "Default" here.
 *
 * @param props - See {@link FontFamilyControlProps}.
 * @return The rendered dropdown.
 * @throws Never.
 */
export function FontFamilyControl({editor}: FontFamilyControlProps) {
  const disabled = editor === undefined;
  const activeFamily: unknown = editor?.getAttributes('textStyle').fontFamily;
  // Only reflect the dropdown's own known values; anything else falls back to
  // "Default" (empty) rather than showing a blank/mismatched <select>.
  const currentFamily =
    typeof activeFamily === 'string' &&
    SLIDE_FONTS.some(font => font.family === activeFamily)
      ? activeFamily
      : '';

  return (
    <span style={{display: 'flex', alignItems: 'center', gap: 6}}>
      <span id="slide-font-family-label" style={{fontSize: 12, color: '#555'}}>
        Font
      </span>
      <select
        aria-labelledby="slide-font-family-label"
        value={currentFamily}
        disabled={disabled}
        onChange={event => {
          const {value} = event.target;
          if (value === '') {
            editor?.chain().focus().unsetFontFamily().run();
            return;
          }
          editor?.chain().focus().setFontFamily(value).run();
        }}
        style={SELECT_STYLE}
      >
        {SLIDE_FONTS.map(font => (
          <option
            key={font.label}
            value={font.family}
            // Preview each option in its own face (Default uses the deck's).
            style={font.family === '' ? undefined : {fontFamily: font.family}}
          >
            {font.label}
          </option>
        ))}
      </select>
    </span>
  );
}
